/**
 * JSON Validate Tool
 * 
 * Validates JSON and reports errors.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

export const JsonValidateTool: ITransformerTool = {
  id: 'json-validate',
  name: 'JSON Validate',
  description: 'Validate JSON and report any errors',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['json', 'validate', 'check', 'verify'],

  seo: {
    slug: 'json-validator',
    pageTitle: 'JSON Validator - Free Online Syntax Checker',
    metaDescription: 'Validate JSON syntax and see detailed error messages. Check structure, types, and key counts instantly. Fast, private, runs entirely in your browser.',
    keywords: ['json validator', 'json syntax checker', 'validate json online', 'json lint', 'json checker'],
    relatedTools: ['json-format', 'json-minify'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes:[SEMANTIC_TYPES.JSON, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  supportsInput(input: string, detectedType?: string): boolean {
    // JsonValidate can work with any text, but prioritize JSON
    if (detectedType === 'JSON') {
      try {
        parseJsonLenient(input);
        return true;
      } catch {
        return true; // Still show for invalid JSON to display errors
      }
    }
    // Accept text too (user might want to validate it)
    return true;
  },

  async transform(input: string): Promise<string> {
    try {
      const parsed = parseJsonLenient(input);
      const type = Array.isArray(parsed) ? 'array' : typeof parsed;
      const count = Array.isArray(parsed) 
        ? parsed.length 
        : typeof parsed === 'object' && parsed !== null 
          ? Object.keys(parsed).length 
          : 0;
      
      return `✓ Valid JSON\nType: ${type}\n${type === 'array' ? `Items: ${count}` : type === 'object' ? `Keys: ${count}` : ''}`;
    } catch (e) {
      const error = e as Error;
      return `✗ Invalid JSON\nError: ${error.message}`;
    }
  },
};

